import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import Hero from './components/Home/Hero';
import Features from './components/Home/Features';
import About from './components/Home/About';
import Services from './components/Home/Services';
import Reviews from './components/Home/Reviews';
import Contact from './components/Home/Contact';
import AiConsultation from './pages/AiConsultation';
import Geo from './pages/Geo';
import Blog from './pages/Blog';
import BlogList from './pages/BlogList';
import WorkflowAutomationBlog from './pages/WorkflowAutomationBlog';

// Import all blog pages
import AiChatbotsCustomerService from './pages/blogs/AiChatbotsCustomerService';
import AutomatedLeadGeneration from './pages/blogs/AutomatedLeadGeneration';
import BusinessProcessAutomation from './pages/blogs/BusinessProcessAutomation';
import AiVoiceAgents from './pages/blogs/AiVoiceAgents';
import AppointmentSchedulingAutomation from './pages/blogs/AppointmentSchedulingAutomation';
import CrmIntegrationAi from './pages/blogs/CrmIntegrationAi';
import AiWebsiteDevelopment from './pages/blogs/AiWebsiteDevelopment';
import EmailMarketingAutomation from './pages/blogs/EmailMarketingAutomation';
import AiImplementationGuide from './pages/blogs/AiImplementationGuide';
import SalesAutomationTools from './pages/blogs/SalesAutomationTools';
import CustomerServiceAutomation from './pages/blogs/CustomerServiceAutomation';
import AiDataAnalytics from './pages/blogs/AiDataAnalytics';
import MarketingAutomationPlatforms from './pages/blogs/MarketingAutomationPlatforms';
import AiSeoOptimization from './pages/blogs/AiSeoOptimization';
import InventoryManagementAutomation from './pages/blogs/InventoryManagementAutomation';
import AiContentGeneration from './pages/blogs/AiContentGeneration';
import SocialMediaAutomation from './pages/blogs/SocialMediaAutomation';
import AiPredictiveAnalytics from './pages/blogs/AiPredictiveAnalytics';
import HrAutomationSystems from './pages/blogs/HrAutomationSystems';
import AiFinancialAnalysis from './pages/blogs/AiFinancialAnalysis';
import EcommerceAutomation from './pages/blogs/EcommerceAutomation';
import AiProjectManagement from './pages/blogs/AiProjectManagement';
import DocumentAutomation from './pages/blogs/DocumentAutomation';
import AiQualityAssurance from './pages/blogs/AiQualityAssurance';
import SupplyChainAutomation from './pages/blogs/SupplyChainAutomation';
import AiCyberSecurity from './pages/blogs/AiCyberSecurity';
import ReportingAutomation from './pages/blogs/ReportingAutomation';
import AiComplianceManagement from './pages/blogs/AiComplianceManagement';
import DatabaseAutomation from './pages/blogs/DatabaseAutomation';
import AiRiskManagement from './pages/blogs/AiRiskManagement';
import NetworkAutomation from './pages/blogs/NetworkAutomation';
import AiPerformanceMonitoring from './pages/blogs/AiPerformanceMonitoring';
import BackupAutomation from './pages/blogs/BackupAutomation';
import AiDecisionSupport from './pages/blogs/AiDecisionSupport';
import CloudAutomation from './pages/blogs/CloudAutomation';
import AiResourceOptimization from './pages/blogs/AiResourceOptimization';
import SecurityAutomation from './pages/blogs/SecurityAutomation';
import AiWorkforceManagement from './pages/blogs/AiWorkforceManagement';
import DevopsAutomation from './pages/blogs/DevopsAutomation';
import AiBusinessIntelligence from './pages/blogs/AiBusinessIntelligence';
import TestingAutomation from './pages/blogs/TestingAutomation';
import AiCustomerInsights from './pages/blogs/AiCustomerInsights';
import DeploymentAutomation from './pages/blogs/DeploymentAutomation';

function App() {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Njord AI Automation Agency",
    "alternateName": "NjordAI",
    "url": "https://www.njordai.co.uk/",
    "logo": "https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png",
    "description": "AI consulting and Generative Engine Optimisation (GEO) agency helping businesses adopt AI strategically and get found in AI-generated search results.",
    "foundingDate": "2024",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2 Saxon Park, Saxon Way East",
      "addressLocality": "Corby",
      "postalCode": "NN18 9EY",
      "addressCountry": "GB"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+447737031147",
        "contactType": "customer service",
        "email": "lennon@njordai.co.uk",
        "availableLanguage": "English"
      }
    ],
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "knowsAbout": [
      "AI Consulting",
      "Generative Engine Optimisation",
      "GEO",
      "AI Visibility",
      "AI Search Optimisation",
      "Business AI Strategy",
      "Workflow Automation"
    ],
    "serviceType": [
      "AI Consulting",
      "Generative Engine Optimisation",
      "GEO Strategy",
      "AI Visibility Audit"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AI & GEO Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Consulting",
            "description": "Strategic AI adoption consulting including workflow audits, opportunity mapping and implementation roadmaps"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Generative Engine Optimisation (GEO)",
            "description": "Get your business appearing in AI-generated answers across ChatGPT, Gemini, Perplexity and other AI search platforms"
          }
        }
      ]
    }
  };

  return (
    <Router>
      <Routes>
        <Route path="/ai-consultation" element={<AiConsultation />} />
        <Route path="/geo" element={<Geo />} />
        <Route path="/blog/ai-consulting-services-for-uk-smes-where-to-start" element={<Blog />} />
        <Route path="/blog/workflow-automation-services-smes-5-processes" element={<WorkflowAutomationBlog />} />
        <Route path="/blog" element={<BlogList />} />
        
        {/* Blog Routes */}
        <Route path="/blog/ai-chatbots-customer-service-complete-guide" element={<AiChatbotsCustomerService />} />
        <Route path="/blog/automated-lead-generation-complete-strategy" element={<AutomatedLeadGeneration />} />
        <Route path="/blog/business-process-automation-ultimate-guide" element={<BusinessProcessAutomation />} />
        <Route path="/blog/ai-voice-agents-future-phone-support" element={<AiVoiceAgents />} />
        <Route path="/blog/appointment-scheduling-automation-complete-guide" element={<AppointmentSchedulingAutomation />} />
        <Route path="/blog/crm-integration-ai-boost-sales-efficiency" element={<CrmIntegrationAi />} />
        <Route path="/blog/ai-website-development-smart-sites-convert" element={<AiWebsiteDevelopment />} />
        <Route path="/blog/email-marketing-automation-complete-strategy" element={<EmailMarketingAutomation />} />
        <Route path="/blog/ai-implementation-guide-step-by-step-smes" element={<AiImplementationGuide />} />
        <Route path="/blog/sales-automation-tools-best-options-uk-smes" element={<SalesAutomationTools />} />
        <Route path="/blog/customer-service-automation-complete-implementation" element={<CustomerServiceAutomation />} />
        <Route path="/blog/ai-data-analytics-transform-business-intelligence" element={<AiDataAnalytics />} />
        <Route path="/blog/marketing-automation-platforms-best-options-smes" element={<MarketingAutomationPlatforms />} />
        <Route path="/blog/ai-seo-optimization-boost-rankings" element={<AiSeoOptimization />} />
        <Route path="/blog/inventory-management-automation-complete-guide" element={<InventoryManagementAutomation />} />
        <Route path="/blog/ai-content-generation-scale-content-strategy" element={<AiContentGeneration />} />
        <Route path="/blog/social-media-automation-complete-strategy" element={<SocialMediaAutomation />} />
        <Route path="/blog/ai-predictive-analytics-forecast-business-success" element={<AiPredictiveAnalytics />} />
        <Route path="/blog/hr-automation-systems-streamline-human-resources" element={<HrAutomationSystems />} />
        <Route path="/blog/ai-financial-analysis-smart-financial-insights" element={<AiFinancialAnalysis />} />
        <Route path="/blog/ecommerce-automation-complete-guide-online-stores" element={<EcommerceAutomation />} />
        <Route path="/blog/ai-project-management-optimize-team-productivity" element={<AiProjectManagement />} />
        <Route path="/blog/document-automation-streamline-paperwork-ai" element={<DocumentAutomation />} />
        <Route path="/blog/ai-quality-assurance-automated-testing-solutions" element={<AiQualityAssurance />} />
        <Route path="/blog/supply-chain-automation-optimize-logistics-ai" element={<SupplyChainAutomation />} />
        <Route path="/blog/ai-cybersecurity-protect-business-ai" element={<AiCyberSecurity />} />
        <Route path="/blog/reporting-automation-automated-business-reports" element={<ReportingAutomation />} />
        <Route path="/blog/ai-compliance-management-automate-regulatory-compliance" element={<AiComplianceManagement />} />
        <Route path="/blog/database-automation-streamline-data-management" element={<DatabaseAutomation />} />
        <Route path="/blog/ai-risk-management-identify-mitigate-business-risks" element={<AiRiskManagement />} />
        <Route path="/blog/network-automation-optimize-it-infrastructure" element={<NetworkAutomation />} />
        <Route path="/blog/ai-performance-monitoring-track-business-metrics" element={<AiPerformanceMonitoring />} />
        <Route path="/blog/backup-automation-secure-data-protection-systems" element={<BackupAutomation />} />
        <Route path="/blog/ai-decision-support-smart-business-decision-making" element={<AiDecisionSupport />} />
        <Route path="/blog/cloud-automation-optimize-cloud-infrastructure" element={<CloudAutomation />} />
        <Route path="/blog/ai-resource-optimization-maximize-business-efficiency" element={<AiResourceOptimization />} />
        <Route path="/blog/security-automation-protect-business-automatically" element={<SecurityAutomation />} />
        <Route path="/blog/ai-workforce-management-optimize-team-performance" element={<AiWorkforceManagement />} />
        <Route path="/blog/devops-automation-streamline-development-operations" element={<DevopsAutomation />} />
        <Route path="/blog/ai-business-intelligence-smart-data-driven-decisions" element={<AiBusinessIntelligence />} />
        <Route path="/blog/testing-automation-automated-quality-assurance" element={<TestingAutomation />} />
        <Route path="/blog/ai-customer-insights-understand-customers-better" element={<AiCustomerInsights />} />
        <Route path="/blog/deployment-automation-streamline-software-releases" element={<DeploymentAutomation />} />
        
        <Route path="/" element={
          <>
            <Helmet>
              <title>Njord | AI Consulting & GEO Agency - Get Found in AI Search</title>
              <meta name="description" content="AI consulting and Generative Engine Optimisation (GEO) agency. We help businesses adopt AI strategically and appear in ChatGPT, Gemini, and Perplexity answers. UK-based." />
              <meta name="keywords" content="AI consulting, GEO, generative engine optimisation, AI search visibility, ChatGPT SEO, AI strategy, UK AI agency" />
              <meta property="og:title" content="Njord | AI Consulting & GEO Agency - Get Found in AI Search" />
              <meta property="og:description" content="AI consulting and Generative Engine Optimisation. Get found in ChatGPT, Gemini, Perplexity and other AI search platforms." />
              <meta property="og:type" content="website" />
              <meta property="og:url" content="https://www.njordai.co.uk/" />
              <meta property="og:image" content="https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content="Njord | AI Consulting & GEO Agency - Get Found in AI Search" />
              <meta name="twitter:description" content="AI consulting and Generative Engine Optimisation. Get found in ChatGPT, Gemini, and Perplexity." />
              <meta name="twitter:image" content="https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png" />
              <link rel="canonical" href="https://www.njordai.co.uk/" />
              
              {/* Organization Schema */}
              <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
              </script>
            </Helmet>
            <div id="hero">
              <Navbar />
              <main>
                <Hero />
                <Services />
                <Features />
                <About />
                <Reviews />
                <Contact />
              </main>
              <Footer />
            </div>
          </>
        } />
      </Routes>
    </Router>
  );
}

export default App;